import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {Input, Textarea} from '@flixbus/honeycomb-react';
import {isBlank} from 'ramda-adjunct';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';
import {useCountryCostsQueryParams} from '@/Pages/RegionalCosts/hooks/useCountryCostsQueryParams';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {
  resetCountryDriverConceptType,
  setCountryDriverConceptType
} from '@/Pages/RegionalCosts/store/slice';
import {validateInputNumber} from '@/Pages/Common/Utils';
import CpcPanel from '@/Pages/Common/CpcPanel';
import ContentLoader from '@/Pages/Common/ContentLoader';
import {saveDriverConceptType} from '@/Pages/RegionalCosts/store/thunks';
import {CountryDriverConceptType} from '@/Pages/RegionalCosts/store/types';

const RegionalCostsDriverConceptTypesPanel: React.FC = () => {
  const countryDriverConceptType = useAppSelector(
    ({regionalCosts}) =>
      regionalCosts.countryDriverConceptTypes.countryDriverConceptType
  );
  const dispatch = useAppDispatch();

  const {countries} = useCountries();
  const {countryId} = useCountryCostsQueryParams();
  const country = useMemo(
    () => countries?.find(({id}) => id === countryId) ?? null,
    [countries, countryId]
  );

  const [showPanel, setShowPanel] = useState(false);

  const [nameIsValid, setNameIsValid] = useState<boolean | undefined>();
  const [multiplicationFactorIsValid, setMultiplicationFactorIsValid] =
    useState<boolean | undefined>();
  const [dailyAllowanceIsValid, setDailyAllowanceIsValid] = useState<
    boolean | undefined
  >();

  useEffect(
    () => setShowPanel(!!countryDriverConceptType),
    [countryDriverConceptType]
  );

  const resetValidationState = useCallback(() => {
    setNameIsValid(undefined);
    setMultiplicationFactorIsValid(undefined);
    setDailyAllowanceIsValid(undefined);
  }, []);

  const closePanel = useCallback(() => {
    setShowPanel(false);
    resetValidationState();
    dispatch(resetCountryDriverConceptType());
  }, [dispatch, resetValidationState]);

  const handleSaveCountryDriverConceptType = useCallback(() => {
    if (!countryDriverConceptType) return;

    resetValidationState();
    let formIsValid = true;

    if (isBlank(countryDriverConceptType.name)) {
      setNameIsValid(false);
      formIsValid = false;
    }

    if (isBlank(countryDriverConceptType.multiplicationFactor)) {
      setMultiplicationFactorIsValid(false);
      formIsValid = false;
    }

    if (isBlank(countryDriverConceptType.dailyAllowance)) {
      setDailyAllowanceIsValid(false);
      formIsValid = false;
    }

    if (formIsValid) {
      dispatch(saveDriverConceptType(countryId));
    }
  }, [countryDriverConceptType, countryId, dispatch, resetValidationState]);

  const onChangeInput = useCallback(
    (
      changeEvent: React.ChangeEvent<HTMLInputElement>,
      field: keyof CountryDriverConceptType
    ) => {
      if (!countryDriverConceptType) return;

      const {value} = changeEvent.target;
      const newValue = validateInputNumber(
        value,
        String(countryDriverConceptType[field])
      );

      dispatch(
        setCountryDriverConceptType({
          ...countryDriverConceptType,
          [field]: newValue ?? null
        })
      );
    },
    [countryDriverConceptType, dispatch]
  );

  return (
    <CpcPanel
      title="Country driver concept type"
      activeAction={showPanel}
      closeAction={closePanel}
      saveAction={handleSaveCountryDriverConceptType}
    >
      {countryDriverConceptType ? (
        <>
          <div>
            <Input
              name="country-driver-concept-type-name"
              id="country-driver-concept-type-name"
              label="Driver concept type"
              valid={nameIsValid}
              value={countryDriverConceptType?.name ?? ''}
              onChange={e =>
                dispatch(
                  setCountryDriverConceptType({
                    ...countryDriverConceptType,
                    name: e.target.value
                  })
                )
              }
            />
          </div>

          <div className={`hcr-space-4-top`}>
            <Textarea
              name="country-driver-concept-type-description"
              id="country-driver-concept-type-description"
              label="Description"
              value={countryDriverConceptType.description ?? ''}
              onChange={e =>
                dispatch(
                  setCountryDriverConceptType({
                    ...countryDriverConceptType,
                    description: e.target.value
                  })
                )
              }
            />
          </div>

          <div className={`hcr-space-4-top`}>
            <Input
              type={`number`}
              name="country-driver-concept-type-multiplicationFactor"
              id="country-driver-concept-type-multiplicationFactor"
              label="Multiplication factor for working hour adjustment"
              valid={multiplicationFactorIsValid}
              value={countryDriverConceptType?.multiplicationFactor ?? ''}
              step={'0.1'}
              onChange={event => onChangeInput(event, 'multiplicationFactor')}
            />
          </div>

          <div className={`hcr-space-4-top`}>
            <Input
              type={`number`}
              name="country-driver-concept-type-dailyAllowance"
              id="country-driver-concept-type-dailyAllowance"
              label="Meals/daily allowance"
              inlineLabelLeft={`${country?.currency}/op. day`}
              placeholder="0.1"
              valid={dailyAllowanceIsValid}
              value={countryDriverConceptType?.dailyAllowance ?? ''}
              onChange={event => onChangeInput(event, 'dailyAllowance')}
              step={`0.1`}
            />
          </div>
        </>
      ) : (
        <ContentLoader />
      )}
    </CpcPanel>
  );
};

export default React.memo(RegionalCostsDriverConceptTypesPanel);
